import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
const fetcher = async (url: string) => {
	const response = await fetch(url)
	if (!response.ok) throw new Error('Rich list temporarily unavailable')
	return response.json()
}
import { NAT_OWNERS_API_URL } from '@/utils/constants'

export interface NatOwnerRow {
	rank: number
	address: string
	balance: number
	transferable: number | null
	locked: number | null
	percent: number | null
	isMiner: boolean
	isWhale: boolean
	minerLabel?: string
	lastMovedAt?: number
}

export interface NatOwnersPage {
	offset: number
	limit: number
	hasMore: boolean
	updatedAt: number
	circulatingSupply: number | null
	totalHolders: number
	whaleThreshold: number | null
	sources: {
		tapalytics: 'ok' | 'blocked' | 'unused'
		natgmi: 'ok' | 'blocked' | 'unused'
		trac: 'ok' | 'blocked' | 'unused'
	}
	holders: NatOwnerRow[]
}

const DEFAULT_TICKER = 'dmt-nat'

const normalizeTicker = (ticker?: string | null) => (ticker || DEFAULT_TICKER).toLowerCase()

const ownersUrl = (ticker: string, offset: number, limit: number) => {
	const query = new URLSearchParams({ ticker, offset: String(offset), limit: String(limit) })
	if (!NAT_OWNERS_API_URL) return `/api/nat-owners?${query}`
	const url = new URL(NAT_OWNERS_API_URL)
	query.forEach((value, key) => url.searchParams.set(key, value))
	return url.toString()
}

/**
 * Single-page fetch. Used by the inline preview (top-N holders) where we
 * don't need pagination and want a stable key for SWR dedupe.
 */
export default function useNatOwners(
	enabled: boolean = true,
	limit: number = 50,
	ticker: string = DEFAULT_TICKER
) {
	const normalizedTicker = normalizeTicker(ticker)
	return useSWR<NatOwnersPage>(enabled ? ownersUrl(normalizedTicker, 0, limit) : null, fetcher, {
		refreshInterval: 300_000,
		revalidateOnFocus: false,
		dedupingInterval: 30_000
	})
}

/**
 * Paginated fetch for the full Alpha table. Each page has `pageSize` rows;
 * call `loadMore()` to append the next page. Stops when `hasMore` is false.
 *
 * Guards:
 *   - Skips loadMore when a fetch is already in flight (prevents multiple
 *     next() triggers from InfiniteScroll firing duplicate setSize calls).
 *   - Skips loadMore when we've already reached the end.
 */
export function useNatOwnersInfinite(
	pageSize: number = 50,
	ticker: string = DEFAULT_TICKER,
	enabled: boolean = true
) {
	const normalizedTicker = normalizeTicker(ticker)
	const getKey = (pageIndex: number, prev: NatOwnersPage | null) => {
		if (!enabled) return null
		if (prev && !prev.hasMore) return null
		return ownersUrl(normalizedTicker, pageIndex * pageSize, pageSize)
	}

	const { data, error, size, setSize, isLoading, isValidating, mutate } =
		useSWRInfinite<NatOwnersPage>(getKey, fetcher, {
			refreshInterval: 300_000,
			revalidateOnFocus: false,
			dedupingInterval: 30_000,
			revalidateFirstPage: false,
			parallel: false
		})

	const pages = data || []
	const holders = Array.from(
		new Map(pages.flatMap((p) => p?.holders || []).map((row) => [row.address, row])).values()
	)
	const lastPage = pages[pages.length - 1]
	const meta = pages[0]

	// True until the server tells us otherwise. Before any page has loaded
	// we can't know yet; once page 1 lands we trust its hasMore.
	const hasMore = lastPage ? !!lastPage.hasMore : !error

	// Fetches are sequential (parallel:false). A page is still loading if
	// we've bumped size but SWR hasn't produced that page yet.
	const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')

	const loadMore = () => {
		if (isLoadingMore || isValidating || !hasMore) return
		setSize((s) => s + 1)
	}

	return {
		holders,
		meta,
		hasMore,
		error,
		isLoading,
		isValidating,
		retry: () => mutate(),
		isLoadingMore,
		loadMore,
		size,
		setSize
	}
}
